import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CashDrawerManager = ({ drawers, activeDrawer, onDrawerChange, onUpdate }) => {
  const { user, currentTenant } = useAuth();
  const [loading, setLoading] = useState(false);
  const [drawerName, setDrawerName] = useState('');
  const [openingBalance, setOpeningBalance] = useState('100.00');
  const [showCreateForm, setShowCreateForm] = useState(false);

  const handleOpenDrawer = async () => {
    if (!drawerName?.trim()) {
      alert('Please enter a drawer name');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase?.from('cash_drawers')?.insert({
          tenant_id: currentTenant?.id,
          name: drawerName?.trim(),
          opened_by: user?.id,
          opened_at: new Date()?.toISOString(),
          opening_balance: parseFloat(openingBalance) || 0,
          current_balance: parseFloat(openingBalance) || 0,
          status: 'open'
        })?.select()?.single();

      if (error) throw error;

      onDrawerChange(data);
      setDrawerName('');
      setOpeningBalance('100.00');
      setShowCreateForm(false);
      onUpdate();
    } catch (error) {
      console.error('Error opening drawer:', error);
      alert('Failed to open drawer');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDrawer = async (drawer) => {
    if (!confirm('Are you sure you want to close this drawer?')) return;

    setLoading(true);
    try {
      const { error } = await supabase?.from('cash_drawers')?.update({
          status: 'closed',
          closed_at: new Date()?.toISOString(),
          closed_by: user?.id
        })?.eq('id', drawer?.id);

      if (error) throw error;

      if (activeDrawer?.id === drawer?.id) {
        onDrawerChange(null);
      }
      onUpdate();
    } catch (error) {
      console.error('Error closing drawer:', error);
      alert('Failed to close drawer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-md p-6 border border-border">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="Wallet" size={24} className="text-primary" />
          <h2 className="text-xl font-bold text-foreground">Cash Drawers</h2>
        </div>
        {!showCreateForm && (
          <Button
            onClick={() => setShowCreateForm(true)}
            size="sm"
            className="flex items-center gap-2"
          >
            <Icon name="Plus" size={16} />
            Open Drawer
          </Button>
        )}
      </div>
      {/* Create Drawer Form */}
      {showCreateForm && (
        <div className="mb-4 p-4 bg-background rounded-lg border border-border">
          <h3 className="font-semibold text-foreground mb-3">Open New Drawer</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Drawer Name
              </label>
              <Input
                value={drawerName}
                onChange={(e) => setDrawerName(e?.target?.value)}
                placeholder="e.g., Main Register, Counter 1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Opening Balance ($)
              </label>
              <Input
                type="number"
                step="0.01"
                value={openingBalance}
                onChange={(e) => setOpeningBalance(e?.target?.value)}
                placeholder="100.00"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleOpenDrawer}
                disabled={loading}
                className="flex-1"
              >
                {loading ? 'Opening...' : 'Open Drawer'}
              </Button>
              <Button
                onClick={() => setShowCreateForm(false)}
                variant="outline"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Active Drawer */}
      {activeDrawer && (
        <div className="mb-4 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Icon name="CheckCircle" size={20} className="text-green-500" />
              <span className="font-bold text-foreground">{activeDrawer?.name}</span>
            </div>
            <span className="text-xs px-2 py-1 bg-green-500/20 text-green-500 rounded-full">
              ACTIVE
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm mb-3">
            <div>
              <p className="text-muted-foreground">Opening Balance</p>
              <p className="font-semibold text-foreground">${activeDrawer?.opening_balance?.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Current Balance</p>
              <p className="font-semibold text-foreground">${activeDrawer?.current_balance?.toFixed(2)}</p>
            </div>
          </div>
          <Button
            onClick={() => handleCloseDrawer(activeDrawer)}
            variant="outline"
            size="sm"
            className="w-full"
            disabled={loading}
          >
            Close Drawer
          </Button>
        </div>
      )}
      {/* Recent Drawers */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-muted-foreground mb-2">Recent Drawers</h3>
        {drawers?.slice(0, 5)?.map((drawer) => (
          <div
            key={drawer?.id}
            className="p-3 bg-background rounded-lg border border-border hover:border-primary/50 transition-colors cursor-pointer"
            onClick={() => drawer?.status === 'open' && onDrawerChange(drawer)}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="font-medium text-foreground">{drawer?.name}</span>
              <span className={`text-xs px-2 py-1 rounded-full ${
                drawer?.status === 'open' ?'bg-green-500/10 text-green-500' :'bg-gray-500/10 text-gray-500'
              }`}>
                {drawer?.status?.toUpperCase()}
              </span>
            </div>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span>Balance: ${drawer?.current_balance?.toFixed(2)}</span>
              <span>{new Date(drawer?.opened_at)?.toLocaleDateString()}</span>
            </div>
          </div>
        ))}
        {drawers?.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">
            No drawers found. Open a new drawer to get started.
          </p>
        )}
      </div>
    </div>
  );
};

export default CashDrawerManager;